import React, { useState, useEffect } from 'react';
import { Add_user, List_user, Update_user, Delete_user, List_country, List_city, MediaUrl, Update_user_status, Admin_refund_user, Admin_login_as_user, WEB_APP_URL } from '../../../Utils/Api';
import { toast } from 'react-toastify';

const User = () => {
    const [users, setUsers] = useState([]);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: '',
        status: '0',
        countryId: '',
        state: '',
        city: '',
        address: '',
        profilePicture: '',
        coverPicture: ''
    });
    const [profileImage, setProfileImage] = useState(null);
    const [profilePreview, setProfilePreview] = useState('');
    const [refundData, setRefundData] = useState({ user: null, amount: '', reason: '' });
    const [editingId, setEditingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleteType, setDeleteType] = useState('soft');
    const [loggingInUserId, setLoggingInUserId] = useState(null);
    const [statusUpdate, setStatusUpdate] = useState({
        show: false,
        userId: null,
        newStatus: null,
        reason: ''
    });

    const handleAdminLoginAsUser = async (user) => {
        if (!user?.email && !user?._id && !user?.id) {
            toast.error("User email or ID is missing");
            return;
        }
        const targetId = user._id || user.id;
        setLoggingInUserId(targetId);
        try {
            const response = await Admin_login_as_user({ email: user.email, userId: targetId });
            if (response?.data?.ssoToken) {
                const ssoToken = response.data.ssoToken;
                const targetUrl = `${WEB_APP_URL}/admin-auth-callback?sso_token=${encodeURIComponent(ssoToken)}`;
                window.open(targetUrl, '_blank');
                toast.success(`Opening session for ${user.name || user.email}...`);
            } else {
                toast.error("Failed to generate login session");
            }
        } catch (error) {
            console.error("Error logging in as user:", error);
            toast.error(error?.response?.data?.message || "Failed to log in as user");
        } finally {
            setLoggingInUserId(null);
        }
    };

    // Search and Filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const roleOptions = [
        { value: 0, label: 'Admin' },
        { value: 1, label: 'Skillster' },
        { value: 2, label: 'Gigster' },
        { value: 3, label: 'Support' },
        { value: 4, label: 'Sales' }
    ];

    const statusOptions = [
        { value: 0, label: 'Active', color: 'success' },
        { value: 1, label: 'Frozen', color: 'info' },
        { value: 2, label: 'Suspended', color: 'warning' },
        { value: 3, label: 'Banned', color: 'danger' },
        { value: 4, label: 'Deleted', color: 'secondary' }
    ];

    useEffect(() => {
        fetchUsers();
        fetchCountries();
    }, [currentPage, itemsPerPage, searchTerm, roleFilter, statusFilter]);

    useEffect(() => {
        if (formData.countryId) {
            fetchCities(formData.countryId);
        } else {
            setCities([]);
        }
    }, [formData.countryId]);

    // Reset to page 1 if any filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, roleFilter, statusFilter]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: itemsPerPage
            };
            if (searchTerm) params.search = searchTerm;
            if (roleFilter !== '') params.role = roleFilter;
            if (statusFilter !== '') params.status = statusFilter;

            const response = await List_user(params);
            if (response && response.data?.users) {
                setUsers(response.data.users);
                setTotalItems(response.data.total || response.data.users.length);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const response = await List_country();
            if (response && response.data?.data) {
                setCountries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    const fetchCities = async (countryId) => {
        try {
            const response = await List_city(countryId);
            if (response && response.data?.data) {
                setCities(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            setCities([]);
        }
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setRoleFilter('');
        setStatusFilter('');
        setCurrentPage(1);
    };

    // Pagination calculations
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = indexOfFirstItem + users.length;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }

            // Check file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            setProfileImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfilePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            toast.error('Please enter user name');
            return;
        }
        if (!formData.email.trim()) {
            toast.error('Please enter email');
            return;
        }
        if (!formData.role) {
            toast.error('Please select a role');
            return;
        }
        if (!formData.countryId) {
            toast.error('Please select a country');
            return;
        }
        if (!editingId && !formData.password) {
            toast.error('Please enter password');
            return;
        }

        setLoading(true);
        try {
            const submitData = new FormData();

            // Append all form data
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    submitData.append(key, formData[key]);
                }
            });

            // Append profile picture if selected
            if (profileImage) {
                submitData.append('profilePicture', profileImage);
            }

            if (editingId) {
                await Update_user(editingId, submitData);
                toast.success('User updated successfully');
            } else {
                await Add_user(submitData);
                toast.success('User added successfully');
            }

            resetForm();
            setShowModal(false);
            fetchUsers();
        } catch (error) {
            console.error('Error saving user:', error);
            toast.error('Failed to save user');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            password: '',
            role: '',
            status: '0',
            countryId: '',
            state: '',
            city: '',
            address: '',
            profilePicture: '',
            coverPicture: ''
        });
        setProfileImage(null);
        setProfilePreview('');
        setEditingId(null);
        setCities([]);
    };

    const handleEdit = (user) => {
        setFormData({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            role: user.role?.toString() || '',
            status: user.status?.toString() || '0',
            countryId: user.countryId?._id || user.countryId || '',
            state: user.state || '',
            city: user.city?._id || user.city || '',
            address: user.address || '',
            profilePicture: user.profilePicture || '',
            coverPicture: user.coverPicture || ''
        });

        // Set profile preview if user has profile picture
        if (user.profilePicture) {
            setProfilePreview(MediaUrl + user.profilePicture);
        } else {
            setProfilePreview('');
        }

        setProfileImage(null);
        setEditingId(user._id || user.id);
        setShowModal(true);
    };

    const handleDelete = async () => {
        if (!deleteId) return;

        setLoading(true);
        try {
            await Delete_user(deleteId, deleteType);
            toast.success('User deleted successfully');
            setDeleteId(null);
            setDeleteType('soft');
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Failed to delete user');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        resetForm();
        setShowModal(true);
    };

    // Pagination handlers
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const getRoleLabel = (role) => {
        const roleOption = roleOptions.find(r => String(r.value) === String(role));
        return roleOption ? roleOption.label : 'Unknown';
    };

    const getStatusLabel = (status, isDeleted = false) => {
        if (isDeleted && (status === undefined || status === null || String(status) === '4')) {
            return 'Deleted';
        }
        const statusOption = statusOptions.find(s => String(s.value) === String(status));
        if (statusOption) return statusOption.label;
        if (isDeleted) return 'Deleted';
        return 'Unknown';
    };

    const getStatusBadgeClass = (status, isDeleted = false) => {
        if (isDeleted && (status === undefined || status === null || String(status) === '4')) {
            return 'bg-secondary-transparent text-secondary';
        }
        const option = statusOptions.find(o => String(o.value) === String(status));
        if (option) return `bg-${option.color}-transparent text-${option.color}`;
        if (isDeleted) return 'bg-secondary-transparent text-secondary';
        return 'bg-secondary-transparent text-secondary';
    };

    // Status Update Handlers
    const initStatusUpdate = (user, newStatus) => {
        if (user.status === newStatus) return;
        setStatusUpdate({
            show: true,
            userId: user._id || user.id,
            newStatus: newStatus,
            reason: ''
        });
    };

    const confirmStatusUpdate = async () => {
        if (!statusUpdate.reason.trim()) {
            toast.error("Please provide a reason for the status change.");
            return;
        }
        setLoading(true);
        try {
            await Update_user_status(statusUpdate.userId, {
                status: statusUpdate.newStatus,
                reason: statusUpdate.reason
            });
            toast.success("User status updated successfully");
            setStatusUpdate({ show: false, userId: null, newStatus: null, reason: '' });
            fetchUsers();
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Failed to update status");
        } finally {
            setLoading(false);
        }
    };

    // Generate page numbers for pagination
    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }
        return pageNumbers;
    };

    const handleRefundSubmit = async (e) => {
        e.preventDefault();
        
        if (!refundData.amount || refundData.amount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                amount: parseFloat(refundData.amount),
                reason: refundData.reason
            };
            
            await Admin_refund_user(refundData.user._id || refundData.user.id, payload);
            toast.success('Refund processed successfully');
            
            // Close modal by clicking close button to rely on bootstrap data-bs-dismiss
            const closeBtn = document.querySelector('#refundModal .btn-close');
            if (closeBtn) {
                closeBtn.click();
            }
            
            setRefundData({ user: null, amount: '', reason: '' });
            fetchUsers();
        } catch (error) {
            console.error('Error processing refund:', error);
            toast.error(error.response?.data?.message || 'Failed to process refund');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="main-content">
            <div className="row my-4 mx-1">
                <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
                    <h1 className="page-title fw-semibold fs-18 mb-0 mt-2">Users</h1>
                    <div className="ms-md-1 ms-0 ">
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item"><a href="#">Management</a></li>
                                <li className="breadcrumb-item active" aria-current="page">Users</li>
                            </ol>
                        </nav>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="card custom-card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">Users List</h6>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleAdd}
                        >
                            <i className="ri-add-line me-1"></i> Add User
                        </button>
                    </div>
                    <div className="card-body">
                        {/* Search and Filter Section */}
                        <div className="row mb-4">
                            <div className="col-md-4">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="ri-search-line"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by name, email, or phone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <select
                                    className="form-select"
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                >
                                    <option value="">All Roles</option>
                                    {roleOptions.map(role => (
                                        <option key={role.value} value={role.value}>
                                            {role.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <select
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    {statusOptions.map(status => (
                                        <option key={status.value} value={status.value}>
                                            {status.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2">
                                <button
                                    className="btn btn-outline-secondary w-100"
                                    onClick={clearFilters}
                                >
                                    <i className="ri-close-line me-1"></i> Clear
                                </button>
                            </div>
                        </div>

                        {/* Results Count */}
                        <div className="mb-3">
                            <span className="text-muted">
                                Showing {totalItems > 0 ? indexOfFirstItem + 1 : 0} to {indexOfLastItem} of {totalItems} users
                            </span>
                        </div>

                        <div className="table-responsive">
                            <table className="table text-nowrap">
                                <thead className="table-primary">
                                    <tr>
                                        <th scope="col">#</th>
                                        <th scope="col">Profile</th>
                                        <th scope="col">Name</th>
                                        <th scope="col">Email</th>
                                        <th scope="col">Phone</th>
                                        <th scope="col">Role</th>
                                        <th scope="col">Country</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Wallet</th>
                                        <th scope="col">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr>
                                            <td colSpan="10" className="text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {!loading && users.length === 0 && (
                                        <tr>
                                            <td colSpan="10" className="text-center py-4">
                                                No users found
                                            </td>
                                        </tr>
                                    )}

                                    {!loading && users.map((user, index) => (
                                        <tr key={user._id || user.id}>
                                            <th scope="row">{indexOfFirstItem + index + 1}</th>
                                            <td>
                                                {user.profilePicture ? (
                                                    <img
                                                        src={MediaUrl + user.profilePicture}
                                                        alt={user.name}
                                                        className="rounded-circle"
                                                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                        style={{ width: '40px', height: '40px' }}
                                                    >
                                                        <i className="ri-user-line text-muted"></i>
                                                    </div>
                                                )}
                                            </td>
                                            <td>{user.name}</td>
                                            <td>{user.email || 'N/A'}</td>
                                            <td>{user.phone || 'N/A'}</td>
                                            <td>{getRoleLabel(user.role)}</td>
                                            <td>{user.countryId?.name || 'N/A'}</td>
                                            <td>
                                                <div className="dropdown">
                                                    <button
                                                        className={`btn btn-sm ${getStatusBadgeClass(user.status, user.isDeleted)} dropdown-toggle`}
                                                        type="button"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded="false"
                                                        style={{ border: 'none' }} // Remove border to look more like a badge if desired
                                                    >
                                                        {getStatusLabel(user.status, user.isDeleted)}
                                                    </button>
                                                    <ul className="dropdown-menu">
                                                        {statusOptions.map(option => (
                                                            <li key={option.value}>
                                                                <button
                                                                    className="dropdown-item"
                                                                    onClick={() => initStatusUpdate(user, option.value)}
                                                                >
                                                                    {option.label}
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="fw-semibold">
                                                    ${((user.walletBalanceCents || 0) / 100).toFixed(2)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="hstack gap-2 fs-15">
                                                    <button
                                                        onClick={() => handleAdminLoginAsUser(user)}
                                                        disabled={loggingInUserId === (user._id || user.id)}
                                                        className="btn btn-icon btn-sm btn-success-transparent rounded-pill"
                                                        title="Login As User in Web App"
                                                    >
                                                        {loggingInUserId === (user._id || user.id) ? (
                                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                        ) : (
                                                            <i className="ri-login-box-line"></i>
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(user)}
                                                        className="btn btn-icon btn-sm btn-info-transparent rounded-pill"
                                                        title="Edit"
                                                    >
                                                        <i className="ri-edit-line"></i>
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setRefundData({ user, amount: '', reason: '' });
                                                        }}
                                                        className="btn btn-icon btn-sm btn-warning-transparent rounded-pill"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#refundModal"
                                                        title="Refund Wallet"
                                                    >
                                                        <i className="ri-refund-2-line"></i>
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteId(user._id || user.id)}
                                                        className="btn btn-icon btn-sm btn-danger-transparent rounded-pill"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#deleteModal"
                                                        title="Delete"
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {!loading && users.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-4">
                                <div className="d-flex align-items-center">
                                    <span className="text-muted me-2">Show:</span>
                                    <select
                                        className="form-select form-select-sm w-auto"
                                        value={itemsPerPage}
                                        onChange={handleItemsPerPageChange}
                                    >
                                        <option value="5">5</option>
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                    <span className="text-muted ms-2">
                                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} entries
                                    </span>
                                </div>

                                <nav>
                                    <ul className="pagination pagination-sm mb-0">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                Previous
                                            </button>
                                        </li>

                                        {renderPageNumbers()}

                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>

                {/* Add/Edit Modal */}
                {showModal && (
                    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {editingId ? 'Edit User' : 'Add New User'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowModal(false)}
                                    ></button>
                                </div>
                                <form onSubmit={handleSubmit} encType="multipart/form-data">
                                    <div className="modal-body">
                                        <div className="row">
                                            {/* Profile Picture */}
                                            <div className="col-12 mb-4">
                                                <label className="form-label">Profile Picture</label>
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3">
                                                        {profilePreview ? (
                                                            <img
                                                                src={profilePreview}
                                                                alt="Profile Preview"
                                                                className="rounded-circle"
                                                                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                                style={{ width: '80px', height: '80px' }}
                                                            >
                                                                <i className="ri-user-line fs-24 text-muted"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <input
                                                            type="file"
                                                            className="form-control"
                                                            accept="image/*"
                                                            onChange={handleImageChange}
                                                        />
                                                        <div className="form-text">
                                                            Recommended size: 200x200 pixels. Max file size: 5MB
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Name */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="name" className="form-label">Name <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="name"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter user name"
                                                    required
                                                />
                                            </div>

                                            {/* Email */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="email" className="form-label">Email <span className="text-danger">*</span></label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter email address"
                                                    required
                                                />
                                            </div>

                                            {/* Phone */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="phone" className="form-label">Phone</label>
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    id="phone"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter phone number"
                                                    pattern="[0-9]{10}"
                                                />
                                            </div>

                                            {/* Password - Only show for new users */}
                                            {!editingId && (
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="password" className="form-label">
                                                        Password <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="password"
                                                        className="form-control"
                                                        id="password"
                                                        name="password"
                                                        value={formData.password}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter password"
                                                        required
                                                    />
                                                </div>
                                            )}

                                            {/* Role */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="role" className="form-label">Role <span className="text-danger">*</span></label>
                                                <select
                                                    className="form-select"
                                                    id="role"
                                                    name="role"
                                                    value={formData.role}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select a role</option>
                                                    {roleOptions.map(role => (
                                                        <option key={role.value} value={role.value}>
                                                            {role.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Status */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="status" className="form-label">Status</label>
                                                <select
                                                    className="form-select"
                                                    id="status"
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                >
                                                    {statusOptions.map(status => (
                                                        <option key={status.value} value={status.value}>
                                                            {status.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Country */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="countryId" className="form-label">Country <span className="text-danger">*</span></label>
                                                <select
                                                    className="form-select"
                                                    id="countryId"
                                                    name="countryId"
                                                    value={formData.countryId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select a country</option>
                                                    {countries.map(country => (
                                                        <option key={country._id} value={country._id}>
                                                            {country.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* City */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="city" className="form-label">City</label>
                                                <select
                                                    className="form-select"
                                                    id="city"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    disabled={!formData.countryId}
                                                >
                                                    <option value="">Select a city</option>
                                                    {cities.map(city => (
                                                        <option key={city._id} value={city._id}>
                                                            {city.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* State */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="state" className="form-label">State</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="state"
                                                    name="state"
                                                    value={formData.state}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter state"
                                                />
                                            </div>

                                            {/* Address */}
                                            <div className="col-12 mb-3">
                                                <label htmlFor="address" className="form-label">Address</label>
                                                <textarea
                                                    className="form-control"
                                                    id="address"
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleInputChange}
                                                    rows="2"
                                                    placeholder="Enter address"
                                                ></textarea>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setShowModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    {editingId ? 'Updating...' : 'Adding...'}
                                                </>
                                            ) : (
                                                editingId ? 'Update' : 'Add'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Refund Modal */}
                <div className="modal fade" id="refundModal" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <form onSubmit={handleRefundSubmit}>
                                <div className="modal-header">
                                    <h5 className="modal-title">Refund User Wallet</h5>
                                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                </div>
                                <div className="modal-body">
                                    {refundData.user && (
                                        <div className="mb-3">
                                            <p className="mb-2"><strong>User:</strong> {refundData.user.name} ({refundData.user.email})</p>
                                            <p className="mb-3 text-warning"><strong>Current Balance:</strong> ${((refundData.user.walletBalanceCents || 0) / 100).toFixed(2)}</p>
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label">Refund Amount ($) <span className="text-danger">*</span></label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            step="0.01"
                                            min="0.01"
                                            required
                                            value={refundData.amount}
                                            onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })}
                                            placeholder="Enter amount to deduct"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Reason for Refund</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={refundData.reason}
                                            onChange={(e) => setRefundData({ ...refundData, reason: e.target.value })}
                                            placeholder="e.g., Requested manual withdrawal"
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                                    <button type="submit" className="btn btn-warning">Process Refund</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                <div className="modal fade" id="deleteModal" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    onClick={() => setDeleteId(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete this user?</p>
                                <div className="mt-3">
                                    <div className="form-check mb-2">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="deleteType"
                                            id="softDelete"
                                            value="soft"
                                            checked={deleteType === 'soft'}
                                            onChange={(e) => setDeleteType(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="softDelete">
                                            <strong>Soft Delete</strong> (Recommended) - Hides user and bans them, but keeps data intact.
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="deleteType"
                                            id="hardDelete"
                                            value="hard"
                                            checked={deleteType === 'hard'}
                                            onChange={(e) => setDeleteType(e.target.value)}
                                        />
                                        <label className="form-check-label text-danger" htmlFor="hardDelete">
                                            <strong>Hard Delete</strong> - Permanently removes user and all their related data (gigs, proposals, etc.).
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                    onClick={() => setDeleteId(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    data-bs-dismiss="modal"
                                    onClick={handleDelete}
                                    disabled={loading}
                                >
                                    {loading ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Status Update Modal */}
                {statusUpdate.show && (
                    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Update User Status</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setStatusUpdate({ ...statusUpdate, show: false })}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <p>Are you sure you want to change the status to <strong>{getStatusLabel(statusUpdate.newStatus)}</strong>?</p>
                                    <div className="mb-3">
                                        <label htmlFor="reason" className="form-label">Reason <span className="text-danger">*</span></label>
                                        <textarea
                                            className="form-control"
                                            id="reason"
                                            rows="3"
                                            value={statusUpdate.reason}
                                            onChange={(e) => setStatusUpdate({ ...statusUpdate, reason: e.target.value })}
                                            placeholder="Please provide a reason for this action..."
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setStatusUpdate({ ...statusUpdate, show: false })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={confirmStatusUpdate}
                                    >
                                        Update Status
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default User;